import React, { useCallback, useEffect, useState } from 'react';
import { orderService, repairService } from '../services/api';
import { GlassButton, GlassCard, GlassInput, GlassSelect } from './GlassUI';
import {
  FaRupeeSign, FaPlus, FaTimes, FaEdit, FaTrash, FaCheck, FaHistory, FaWallet,
} from 'react-icons/fa';

/**
 * Manual payment ledger for Orders (New Sales) and Repairs.
 *
 * Staff type every entry in by hand — an advance at booking, a part payment
 * while the job is in progress, the final settlement — and mark each one PAID
 * or UNPAID. The CRM only records and totals them; it never calculates them.
 * Every entry can be edited or deleted and the full history is always shown.
 */

export interface ModulePayment {
  id: string;
  amount: number;
  kind: string; // ADVANCE | PAYMENT | FINAL
  status: string; // PAID | UNPAID
  method?: string | null;
  note?: string | null;
  paidAt: string;
  recordedBy?: string | null;
}

export interface PaymentSummary {
  totalPaid: number;
  totalUnpaid: number;
  advancePaid: number;
  paidCount: number;
  unpaidCount: number;
  balanceAfter: number;
}

interface PaymentsPanelProps {
  kind: 'order' | 'repair';
  recordId: string;
  totalAmount?: number;
  onChange?: (summary: PaymentSummary) => void;
}

const METHODS = [
  { value: 'CASH', label: 'Cash' },
  { value: 'UPI', label: 'UPI' },
  { value: 'CARD', label: 'Card' },
  { value: 'CHEQUE', label: 'Cheque' },
  { value: 'ONLINE', label: 'Online Transfer' },
  { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
  { value: 'OTHER', label: 'Other' },
];

const KINDS = [
  { value: 'ADVANCE', label: 'Advance' },
  { value: 'PAYMENT', label: 'Balance Payment' },
  { value: 'FINAL', label: 'Final Settlement' },
];

const money = (n: number) =>
  '₹' + new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(Number(n) || 0);

const dateStr = (d: string) => {
  const dt = new Date(d);
  return Number.isNaN(dt.getTime())
    ? '—'
    : dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const todayInput = () => new Date().toISOString().slice(0, 10);

const emptyForm = () => ({
  amount: '',
  kind: 'ADVANCE',
  status: 'PAID',
  method: 'CASH',
  paidAt: todayInput(),
  note: '',
});

const statusChip = (status: string) =>
  status === 'PAID'
    ? 'bg-emerald-100 text-emerald-700'
    : 'bg-red-100 text-red-700';

const kindChip = (kind: string) =>
  kind === 'ADVANCE'
    ? 'bg-blue-100 text-blue-700'
    : kind === 'FINAL'
      ? 'bg-purple-100 text-purple-700'
      : 'bg-slate-200 text-slate-600';

const PaymentsPanel: React.FC<PaymentsPanelProps> = ({ kind, recordId, totalAmount, onChange }) => {
  const service = kind === 'order' ? orderService : repairService;

  const [payments, setPayments] = useState<ModulePayment[]>([]);
  const [summary, setSummary] = useState<PaymentSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm());

  const applyResult = useCallback(
    (data: { payments?: ModulePayment[]; summary?: PaymentSummary }) => {
      if (data.payments) setPayments(data.payments);
      if (data.summary) {
        setSummary(data.summary);
        onChange?.(data.summary);
      }
    },
    [onChange],
  );

  const load = useCallback(async () => {
    if (!recordId) return;
    setLoading(true);
    setError('');
    try {
      const res = await service.getPayments(recordId);
      applyResult(res.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load payment history');
    } finally {
      setLoading(false);
    }
  }, [recordId, service, applyResult]);

  useEffect(() => {
    load();
  }, [load]);

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm());
    setError('');
    setShowForm(true);
  };

  const openEdit = (p: ModulePayment) => {
    setEditingId(p.id);
    setForm({
      amount: String(p.amount),
      kind: p.kind || 'PAYMENT',
      status: p.status || 'PAID',
      method: p.method || 'CASH',
      paidAt: (p.paidAt || '').slice(0, 10) || todayInput(),
      note: p.note || '',
    });
    setError('');
    setShowForm(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!(Number(form.amount) > 0)) {
      setError('Enter an amount greater than zero');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        amount: Number(form.amount),
        kind: form.kind,
        status: form.status,
        method: form.method,
        paidAt: form.paidAt,
        note: form.note || null,
      };
      const res = editingId
        ? await service.updatePayment(recordId, editingId, payload)
        : await service.addPayment(recordId, payload);
      applyResult(res.data);
      setShowForm(false);
      setEditingId(null);
      setForm(emptyForm());
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save payment');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (p: ModulePayment) => {
    if (!window.confirm(`Delete this ${money(p.amount)} ${p.status.toLowerCase()} entry?`)) return;
    setBusyId(p.id);
    setError('');
    try {
      const res = await service.deletePayment(recordId, p.id);
      applyResult(res.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete payment');
    } finally {
      setBusyId(null);
    }
  };

  const toggleStatus = async (p: ModulePayment) => {
    setBusyId(p.id);
    setError('');
    try {
      const res = await service.updatePayment(recordId, p.id, {
        status: p.status === 'PAID' ? 'UNPAID' : 'PAID',
      });
      applyResult(res.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update payment');
    } finally {
      setBusyId(null);
    }
  };

  const total = Number(totalAmount) || 0;
  const s = summary;

  return (
    <GlassCard className="p-5">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <h3 className="font-bold text-slate-900 flex items-center gap-2">
          <FaWallet className="text-emerald-600" /> Payments &amp; Advance
          <span className="text-xs font-normal text-slate-400">(manual entries)</span>
        </h3>
        <GlassButton variant="primary" onClick={openAdd} className="text-sm">
          <FaPlus className="mr-1" /> Add Entry
        </GlassButton>
      </div>

      {/* Total / Advance / Balance roll-up */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">Total Amount</p>
          <p className="text-lg font-bold text-slate-800">{total > 0 ? money(total) : '—'}</p>
        </div>
        <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-3">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Advance / Paid</p>
          <p className="text-lg font-bold text-emerald-700">{money(s?.totalPaid || 0)}</p>
        </div>
        <div className="rounded-xl bg-red-50 border border-red-100 p-3">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-red-700">Balance To Pay</p>
          <p className="text-lg font-bold text-red-700">{money(s?.balanceAfter || 0)}</p>
        </div>
      </div>

      {error && (
        <div className="mb-3 flex items-start gap-2 text-sm rounded-xl px-3 py-2 bg-red-50 border border-red-200 text-red-700">
          <span className="break-all">{error}</span>
          <button className="ml-auto text-xs" onClick={() => setError('')}>
            <FaTimes />
          </button>
        </div>
      )}

      {/* Add / edit form */}
      {showForm && (
        <form onSubmit={handleSave} className="mb-4 rounded-2xl border border-amber-200 bg-amber-50/60 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="font-semibold text-slate-800 text-sm">
              {editingId ? 'Edit payment entry' : 'New payment entry'}
            </p>
            <button
              type="button"
              onClick={() => { setShowForm(false); setEditingId(null); }}
              className="text-slate-400 hover:text-slate-600"
            >
              <FaTimes />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">
                <FaRupeeSign className="inline mr-1" /> Advance / Amount (₹)
              </label>
              <GlassInput
                type="number"
                step="0.01"
                min="0"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                placeholder="0.00"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Date</label>
              <GlassInput
                type="date"
                value={form.paidAt}
                onChange={(e) => setForm({ ...form, paidAt: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Type</label>
              <GlassSelect value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
                {KINDS.map((k) => (
                  <option key={k.value} value={k.value}>{k.label}</option>
                ))}
              </GlassSelect>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Method</label>
              <GlassSelect value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })}>
                {METHODS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </GlassSelect>
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-slate-600 mb-1">Note</label>
              <GlassInput
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                placeholder="Optional"
              />
            </div>
          </div>

          {/* Live balance preview: total − advance/paid */}
          {total > 0 && (
            <div className="flex items-center justify-between text-sm rounded-xl bg-white border border-slate-200 px-3 py-2">
              <span className="text-slate-500">Balance To Pay after this entry</span>
              <span className="font-bold text-red-600">
                {money(Math.max(0, total - ((s?.totalPaid || 0) + (Number(form.amount) || 0))))}
              </span>
            </div>
          )}

          <div className="flex gap-2">
            <GlassButton type="submit" variant="primary" disabled={saving} className="flex-1">
              {saving ? 'Saving...' : editingId ? 'Update Entry' : 'Save Entry'}
            </GlassButton>
            <GlassButton type="button" variant="secondary" onClick={() => { setShowForm(false); setEditingId(null); }}>
              Cancel
            </GlassButton>
          </div>
        </form>
      )}

      {/* History */}
      <div className="flex items-center gap-2 mb-2 text-slate-500">
        <FaHistory size={12} />
        <span className="text-xs font-semibold uppercase tracking-wide">Payment history</span>
      </div>

      {loading ? (
        <p className="text-sm text-slate-400 py-4 text-center">Loading...</p>
      ) : payments.length === 0 ? (
        <p className="text-sm text-slate-400 py-4 text-center">
          No payment entries yet. Add the advance or payment you have taken.
        </p>
      ) : (
        <div className="space-y-2">
          {payments.map((p) => (
            <div
              key={p.id}
              className="flex items-start justify-between gap-3 rounded-xl border border-slate-200 bg-white p-3"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-slate-900">{money(p.amount)}</span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${kindChip(p.kind)}`}>
                    {p.kind}
                  </span>
                  <button
                    onClick={() => toggleStatus(p)}
                    disabled={busyId === p.id}
                    title="Click to flip Paid / Unpaid"
                    className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${statusChip(p.status)} disabled:opacity-50`}
                  >
                    {p.status}
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  {dateStr(p.paidAt)}
                  {p.method ? ` · ${p.method}` : ''}
                  {p.note ? ` · ${p.note}` : ''}
                </p>
              </div>

              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={() => openEdit(p)}
                  className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                  title="Edit entry"
                >
                  <FaEdit size={13} />
                </button>
                <button
                  onClick={() => handleDelete(p)}
                  disabled={busyId === p.id}
                  className="p-2 rounded-lg text-red-500 hover:bg-red-50 hover:text-red-700 disabled:opacity-40"
                  title="Delete entry"
                >
                  <FaTrash size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {s && payments.length > 0 && (
        <div className="mt-3 flex items-center gap-2 text-xs text-slate-500">
          <FaCheck className="text-emerald-500" />
          {s.paidCount} paid · {s.unpaidCount} unpaid
        </div>
      )}
    </GlassCard>
  );
};

export default PaymentsPanel;
